import { environment } from '../environments/environment';

/**
 * Redirect bridge plumbing for msal-browser v5.
 *
 * Deliberately imports nothing from `@azure/msal-*` or the Angular application at
 * module scope: `main.ts` consults this before deciding whether to bootstrap, so it
 * must stay cheap enough to sit in the entry chunk. The MSAL bridge itself is pulled
 * in through a dynamic import only on the bridge path.
 */

/** Trailing slashes are insignificant when comparing routes. */
const normalizePath = (path: string): string => (path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path);

/**
 * Path of the redirect bridge page, derived from the configured `redirectUri` so that
 * the two can never drift apart.
 */
export const msalRedirectBridgePath = normalizePath(new URL(environment.msal.redirectUrl).pathname);

/**
 * True when the current document is the redirect bridge page.
 *
 * The bridge must not boot the application or touch MSAL. It is loaded in a hidden
 * iframe on every silent token renewal and shares `sessionStorage` with the top frame,
 * so initializing MSAL here would let it consume or clobber the pending interaction
 * state of the main application.
 */
export const isMsalRedirectBridgePath = (pathname: string = window.location.pathname): boolean => normalizePath(pathname) === msalRedirectBridgePath;

/**
 * Forwards the IdP response from the bridge page back to the main application.
 *
 * For popup and silent flows this posts the response over a `BroadcastChannel`; for
 * the redirect flow it caches the payload and navigates back to the originating URL.
 */
export async function runMsalRedirectBridge(): Promise<void> {
	const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');

	try {
		await broadcastResponseToMainFrame();
	} catch (error) {
		// Nothing can recover this in-page; the main frame falls back to interactive auth.
		console.error('MSAL redirect bridge error:', error);
	}
}
